/**
 * Generic caching service with timestamp-based expiration
 * Supports both memory and localStorage with automatic cleanup
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiryDuration: number; // in milliseconds
}

interface CacheConfig {
  maxMemoryEntries: number;
  maxLocalStorageSize: number; // in bytes
  keyPrefix: string;
}

export class CacheService {
  private memoryCache = new Map<string, CacheEntry<any>>();
  private readonly config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      maxMemoryEntries: 50,
      maxLocalStorageSize: 5 * 1024 * 1024, // 5MB
      keyPrefix: 'aurora_cache_',
      ...config
    };
  }

  /**
   * Store data in cache with expiration
   */
  async set<T>(key: string, data: T, expiryDurationMs: number): Promise<void> {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiryDuration: expiryDurationMs
    };

    // Store in memory cache
    this.memoryCache.set(key, entry);
    this.cleanupMemoryCache();

    // Store in localStorage
    try {
      const storageKey = this.config.keyPrefix + key;
      const serialized = JSON.stringify(entry);

      // Check storage size before adding
      await this.ensureStorageSpace(serialized.length);
      localStorage.setItem(storageKey, serialized);
    } catch (error) {
      console.warn('Failed to store in localStorage:', error);
      // Continue with memory cache only
    }
  }

  /**
   * Retrieve data from cache if not expired
   */
  async get<T>(key: string): Promise<T | null> {
    // Check memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && this.isValidEntry(memoryEntry)) {
      return memoryEntry.data as T;
    }

    // Check localStorage
    try {
      const storageKey = this.config.keyPrefix + key;
      const serialized = localStorage.getItem(storageKey);

      if (serialized) {
        const entry: CacheEntry<T> = JSON.parse(serialized);

        if (this.isValidEntry(entry)) {
          // Refresh memory cache
          this.memoryCache.set(key, entry);
          return entry.data;
        } else {
          // Remove expired entry
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return null;
  }

  /**
   * Check if data exists and is not expired
   */
  async has(key: string): Promise<boolean> {
    const data = await this.get(key);
    return data !== null;
  }

  /**
   * Remove specific cache entry
   */
  async invalidate(key: string): Promise<void> {
    this.memoryCache.delete(key);

    try {
      const storageKey = this.config.keyPrefix + key;
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  }

  /**
   * Clear all cache entries
   */
  async clear(): Promise<void> {
    this.memoryCache.clear();

    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.keyPrefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { memoryEntries: number; localStorageEntries: number; estimatedSize: number } {
    let localStorageEntries = 0;
    let estimatedSize = 0;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.keyPrefix)) {
          localStorageEntries++;
          const value = localStorage.getItem(key);
          if (value) {
            estimatedSize += value.length * 2; // Rough estimate (UTF-16)
          }
        }
      }
    } catch (error) {
      console.warn('Failed to get localStorage stats:', error);
    }

    return {
      memoryEntries: this.memoryCache.size,
      localStorageEntries,
      estimatedSize
    };
  }

  /**
   * Check if cache entry is still valid (not expired)
   */
  private isValidEntry<T>(entry: CacheEntry<T>): boolean {
    const now = Date.now();
    return (now - entry.timestamp) < entry.expiryDuration;
  }

  /**
   * Clean up memory cache if it exceeds max entries
   */
  private cleanupMemoryCache(): void {
    if (this.memoryCache.size <= this.config.maxMemoryEntries) {
      return;
    }

    // Remove oldest entries
    const entries = Array.from(this.memoryCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    const entriesToRemove = entries.slice(0, entries.length - this.config.maxMemoryEntries);
    entriesToRemove.forEach(([key]) => this.memoryCache.delete(key));
  }

  /**
   * Ensure there's enough space in localStorage
   */
  private async ensureStorageSpace(requiredSize: number): Promise<void> {
    const stats = this.getStats();

    if (stats.estimatedSize + requiredSize <= this.config.maxLocalStorageSize) {
      return;
    }

    // Clean up expired entries first
    await this.cleanupExpiredEntries();

    // If still not enough space, remove oldest entries
    const updatedStats = this.getStats();
    if (updatedStats.estimatedSize + requiredSize > this.config.maxLocalStorageSize) {
      await this.removeOldestEntries();
    }
  }

  /**
   * Remove expired entries from localStorage
   */
  private async cleanupExpiredEntries(): Promise<void> {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.keyPrefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const entry: CacheEntry<any> = JSON.parse(value);
              if (!this.isValidEntry(entry)) {
                keysToRemove.push(key);
              }
            } catch (parseError) {
              // Invalid entry, remove it
              keysToRemove.push(key);
            }
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to cleanup expired entries:', error);
    }
  }

  /**
   * Remove oldest cache entries to free up space
   */
  private async removeOldestEntries(): Promise<void> {
    try {
      const entries: Array<{ key: string; timestamp: number }> = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.keyPrefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const entry: CacheEntry<any> = JSON.parse(value);
              entries.push({ key, timestamp: entry.timestamp });
            } catch (parseError) {
              // Invalid entry, will be removed
              entries.push({ key, timestamp: 0 });
            }
          }
        }
      }

      // Sort by timestamp and remove oldest 25%
      entries.sort((a, b) => a.timestamp - b.timestamp);
      const toRemove = Math.ceil(entries.length * 0.25);

      for (let i = 0; i < toRemove; i++) {
        localStorage.removeItem(entries[i].key);
      }
    } catch (error) {
      console.warn('Failed to remove oldest entries:', error);
    }
  }
}

// Export singleton instance
export const cacheService = new CacheService();